import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import AchievementImage from "@/components/AchievementImage";
import {
  Trophy,
  Lock,
  Unlock,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
  Search,
  X,
  SortAsc,
  SortDesc,
  LayoutGrid,
  LayoutList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PlatformBadge, { SystemBadge } from "@/components/PlatformBadge";
import { MilestoneBadge } from "@/components/GameDetail/AchievementCard";
import { AdBanner } from "@/components/AdSense";

const API = `${getApiUrl()}/api`;

// Helper to capitalize platform names
const capitalizePlatform = (platform) => {
  if (!platform) return platform;
  const capitalizations = {
    xbox: "Xbox",
    playstation: "PlayStation",
    steam: "Steam",
    epic: "Epic",
    nintendo: "Nintendo",
    pc: "PC",
    other: "Other",
  };
  return capitalizations[platform.toLowerCase()] || platform.charAt(0).toUpperCase() + platform.slice(1);
};

export default function AchievementsBrowse() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    platforms: [],
    systems: [],
    games: [],
    tags: [],
  });
  const [viewMode, setViewMode] = useState(null); // null until preference loaded, then "list" or "tile"
  const [pageInput, setPageInput] = useState("");
  const [milestoneMap, setMilestoneMap] = useState({});
  
  // Fetch user's view mode preference from settings
  useEffect(() => {
    const fetchViewModePreference = async () => {
      try {
        const response = await axios.get(`${API}/settings/preferences`);
        // Map "grid" to "tile" for this page's terminology
        const preferredMode = response.data.achievementViewMode === "grid" ? "tile" : "list";
        setViewMode(preferredMode);
      } catch (error) {
        console.error("Failed to fetch view mode preference:", error);
        setViewMode("list"); // Default to list on error
      }
    };
    fetchViewModePreference();
  }, []);
  
  // Filter state from URL params
  const status = searchParams.get("status") || "unlocked";
  const platform = searchParams.get("platform") || "";
  const system = searchParams.get("system") || "";
  const gameId = searchParams.get("gameId") || "";
  const tag = searchParams.get("tag") || "";
  const sortBy = searchParams.get("sortBy") || (status === "unlocked" ? "earnedAt" : "name");
  const sortOrder = searchParams.get("sortOrder") || "desc";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Fetch achievements
  const fetchAchievements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      params.set("page", page.toString());
      params.set("limit", "100");
      
      if (platform) params.set("platform", platform);
      if (system) params.set("system", system);
      if (gameId) params.set("gameId", gameId);
      if (tag) params.set("tag", tag);
      
      const response = await axios.get(`${API}/achievements-browse?${params.toString()}`);
      setAchievements(response.data.achievements);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
      setFilters(response.data.filters);
    } catch (error) {
      console.error("Failed to fetch achievements:", error);
      toast.error("Failed to load achievements");
    } finally {
      setLoading(false);
    }
  }, [status, platform, system, gameId, tag, sortBy, sortOrder, page]);
  
  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);

  // Fetch milestone map once
  useEffect(() => {
    const fetchMilestoneMap = async () => {
      try {
        const response = await axios.get(`${API}/stats/milestone-map`);
        setMilestoneMap(response.data.milestoneMap || {});
      } catch (error) { /* Non-critical */ }
    };
    fetchMilestoneMap();
  }, []);
  
  // Update URL params
  const updateParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    // Reset page when filters change
    if (!updates.page) {
      newParams.set("page", "1");
    }
    setSearchParams(newParams);
    if (updates.page) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };
    } catch {
      return null;
    }
  };
  
  // Get score display based on platform
  const getScoreDisplay = (ach, compact = false) => {
    const platform = ach.gamePlatform?.toLowerCase();
    
    // PlayStation: Show trophy type (Bronze, Silver, Gold, Platinum)
    if (platform === "playstation") {
      const trophyColors = {
        bronze: "text-amber-600",
        silver: "text-gray-400",
        gold: "text-yellow-400",
        platinum: "text-cyan-300",
      };
      const trophyType = ach.trophyType?.toLowerCase() || "bronze";
      return (
        <span className={`font-bold ${trophyColors[trophyType] || "text-amber-600"} ${compact ? "text-xs" : ""}`}>
          {trophyType.charAt(0).toUpperCase() + trophyType.slice(1)}
        </span>
      );
    }
    
    // Steam: No points display
    if (platform === "steam") {
      return null;
    }
    
    // Epic: Show XP
    if (platform === "epic") {
      return (
        <span className={`font-bold text-purple-400 ${compact ? "text-xs" : ""}`}>
          {ach.score || 0} XP
        </span>
      );
    }
    
    // Xbox (and others): Show Gamerscore (G)
    return (
      <span className={`font-bold text-yellow-400 ${compact ? "text-xs" : ""}`}>
        {ach.score || 0} G
      </span>
    );
  };
  
  // Filter achievements by search query
  const filteredAchievements = searchQuery
    ? achievements.filter(a => 
        a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.gameName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : achievements;
  
  // Clear all filters
  const clearFilters = () => {
    setSearchParams({ status });
    setSearchQuery("");
  };
  
  const hasActiveFilters = platform || system || gameId || tag || searchQuery;
  
  // Handle page input submission
  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const pageNum = parseInt(pageInput, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      updateParams({ page: pageNum.toString() });
      setPageInput("");
    } else {
      toast.error(`Please enter a page number between 1 and ${totalPages}`);
    }
  };
  
  // Pagination controls — inlined as JSX (NOT a nested component) so the
  // <Input> isn't unmounted/remounted on every parent render. A nested
  // component declaration re-creates the function identity on each render,
  // which made the page-number field lose focus after every keystroke.
  const paginationControls = (
    <div className="flex items-center justify-center gap-2 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        onClick={() => updateParams({ page: "1" })}
        disabled={page <= 1}
        className="bg-[#121212] border-white/10"
        title="First Page"
        data-testid="pagination-first"
      >
        <ChevronsLeft className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => updateParams({ page: Math.max(1, page - 1).toString() })}
        disabled={page <= 1}
        className="bg-[#121212] border-white/10"
        data-testid="pagination-prev"
      >
        <ChevronLeft className="w-4 h-4" />
        Prev
      </Button>

      <form onSubmit={handlePageInputSubmit} className="flex items-center gap-2">
        <span className="text-white/60">Page</span>
        <Input
          type="number"
          min={1}
          max={totalPages}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onFocus={(e) => {
            // Pre-fill with current page so the user can edit any digit
            // immediately and select-all so typing replaces.
            if (!pageInput) setPageInput(String(page));
            requestAnimationFrame(() => e.target.select());
          }}
          onBlur={() => {
            // If left empty / unchanged, clear so placeholder shows again
            if (pageInput === "" || pageInput === String(page)) {
              setPageInput("");
            }
          }}
          placeholder={page.toString()}
          className="w-20 h-8 text-center bg-[#121212] border-white/10"
          data-testid="pagination-input"
        />
        <span className="text-white/60">of {totalPages.toLocaleString()}</span>
        <Button
          type="submit"
          variant="outline"
          size="sm"
          className="h-8 bg-[#121212] border-white/10"
          disabled={!pageInput || parseInt(pageInput, 10) === page}
          data-testid="pagination-go"
        >
          Go
        </Button>
      </form>

      <Button
        variant="outline"
        size="sm"
        onClick={() => updateParams({ page: Math.min(totalPages, page + 1).toString() })}
        disabled={page >= totalPages}
        className="bg-[#121212] border-white/10"
        data-testid="pagination-next"
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => updateParams({ page: totalPages.toString() })}
        disabled={page >= totalPages}
        className="bg-[#121212] border-white/10"
        title="Last Page"
        data-testid="pagination-last"
      >
        <ChevronsRight className="w-4 h-4" />
      </Button>
    </div>
  );
  
  return (
    <div data-testid="achievements-browse-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
            Achievements & Trophies
          </h1>
          <p className="text-white/50 mt-1">
            {total.toLocaleString()} {status === "unlocked" ? "unlocked" : "locked"} achievements
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-[#121212] rounded-md p-1">
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-8 px-2"
              title="List View"
            >
              <LayoutList className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "tile" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("tile")}
              className="h-8 px-2"
              title="Tile View"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Status Toggle */}
          <Tabs value={status} onValueChange={(v) => updateParams({ status: v, sortBy: v === "unlocked" ? "earnedAt" : "name" })}>
            <TabsList className="bg-[#121212]">
              <TabsTrigger value="unlocked" className="data-[state=active]:bg-green-600">
                <Unlock className="w-4 h-4 mr-2" />
                Unlocked
              </TabsTrigger>
              <TabsTrigger value="locked" className="data-[state=active]:bg-red-600/80">
                <Lock className="w-4 h-4 mr-2" />
                Locked
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      
      {/* Filters Bar */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search achievements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-[#121212] border-white/10"
              />
            </div>
            
            {/* Platform Filter */}
            <Select value={platform} onValueChange={(v) => updateParams({ platform: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[140px] bg-[#121212] border-white/10">
                <SelectValue placeholder="Platform" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Platforms</SelectItem>
                {filters.platforms.map(p => (
                  <SelectItem key={p} value={p}>{capitalizePlatform(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* System Filter */}
            <Select value={system} onValueChange={(v) => updateParams({ system: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[150px] bg-[#121212] border-white/10">
                <SelectValue placeholder="System" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all">All Systems</SelectItem>
                {filters.systems.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Game Filter - Search */}
            <GameSearchFilter
              games={filters.games}
              value={gameId}
              onChange={(v) => updateParams({ gameId: v })}
            />
            
            {/* Tag Filter */}
            {filters.tags.length > 0 && (
              <Select value={tag} onValueChange={(v) => updateParams({ tag: v === "all" ? "" : v })}>
                <SelectTrigger className="w-[140px] bg-[#121212] border-white/10">
                  <SelectValue placeholder="Tag" />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="all">All Tags</SelectItem>
                  {filters.tags.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Sort */}
            <Select value={sortBy} onValueChange={(v) => updateParams({ sortBy: v })}>
              <SelectTrigger className="w-[140px] bg-[#121212] border-white/10">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {status === "unlocked" && <SelectItem value="earnedAt">Unlock Date</SelectItem>}
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="score">Score</SelectItem>
                <SelectItem value="rarity">Rarity</SelectItem>
                <SelectItem value="game">Game</SelectItem>
                <SelectItem value="platform">Platform</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Sort Order */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => updateParams({ sortOrder: sortOrder === "desc" ? "asc" : "desc" })}
              className="bg-[#121212] border-white/10 hover:bg-white/10"
              title={sortOrder === "desc" ? "Descending" : "Ascending"}
            >
              {sortOrder === "desc" ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
            </Button>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-white/50 hover:text-white"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Top Pagination */}
      {totalPages > 1 && paginationControls}
      
      {/* Achievement List */}
      {loading || viewMode === null ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
      ) : filteredAchievements.length === 0 ? (
        <div className="text-center py-20">
          <Trophy className="w-16 h-16 text-white/10 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white/60">No achievements found</h3>
          <p className="text-white/40 mt-1">Try adjusting your filters</p>
        </div>
      ) : viewMode === "tile" ? (
        /* Tile/Grid View - Compact 10 across */
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 lg:grid-cols-8 xl:grid-cols-10 gap-2">
          {filteredAchievements.map((ach) => {
            const datetime = formatDateTime(ach.earnedAt);
            const achMs = milestoneMap[ach.id] || null;
            const msColor = achMs ? (achMs[0].platform ? ({xbox:"#107C10",playstation:"#2E6DB4",steam:"#66C0F4"}[achMs[0].platform] || "#7C3AED") : ({achievement:"#7C3AED",score:"#F59E0B"}[achMs[0].type] || "#7C3AED")) : null;
            return (
              <TooltipProvider key={ach.id}>
                <Tooltip delayDuration={150}>
                  <TooltipTrigger asChild>
                    <div
                      className={`cursor-pointer group ${ach.isUnlocked ? "" : "opacity-50 grayscale"}`}
                      onClick={() => navigate(`/games/${ach.targetGameId || ach.gameId}`)}
                      data-testid={`achievement-tile-${ach.id}`}
                    >
                      <div
                        className="relative w-full aspect-square rounded-lg overflow-hidden bg-[#1a1a1a] hover:ring-2 hover:ring-white/30 transition-all"
                        style={achMs ? { boxShadow: `inset 0 0 0 2px ${msColor}70, 0 0 6px ${msColor}30` } : undefined}
                      >
                        {ach.imageUrl ? (
                          <AchievementImage
                            src={ach.imageUrl}
                            additionalImages={ach.additionalImages}
                            alt={ach.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-white/20" />
                          </div>
                        )}
                        {(ach.isDlcAchievement || ach.dlcName) && (
                          <span
                            className="absolute top-1 right-1 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-purple-500/30 text-purple-100 backdrop-blur-sm border border-purple-300/30 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`achievement-tile-dlc-badge-${ach.id}`}
                          >
                            DLC
                          </span>
                        )}
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs bg-[#1a1a1a] border-white/10 p-3">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-medium text-white">{ach.name}</p>
                        {getScoreDisplay(ach)}
                      </div>
                      <p className="text-white/60 text-sm">{ach.description || "No description"}</p>
                      <p className="text-white/80 text-sm">{ach.gameName}</p>
                      {ach.dlcName && (
                        <p className="text-purple-300 text-xs flex items-center gap-1">
                          <span className="px-1 py-0.5 text-[9px] rounded bg-purple-500/20 border border-purple-300/30 uppercase tracking-wide">DLC</span>
                          <span className="truncate">{ach.dlcName}</span>
                        </p>
                      )}
                      {datetime && (
                        <p className="text-white/40 text-xs">
                          Unlocked: {datetime.date} {datetime.time}
                        </p>
                      )}
                      <div className="flex items-center gap-1 pt-1">
                        {ach.gamePlatform && <PlatformBadge platform={ach.gamePlatform} size="sm" />}
                        {ach.gameSystem && <SystemBadge system={ach.gameSystem} platform={ach.gamePlatform} size="sm" />}
                      </div>
                      {achMs && (
                        <div className="border-t border-white/10 pt-1 mt-1 space-y-0.5">
                          {achMs.map((m, i) => (
                            <p key={i} className="text-[10px] font-medium" style={{ color: m.platform ? ({xbox:"#107C10",playstation:"#2E6DB4",steam:"#66C0F4"}[m.platform] || "#7C3AED") : ({achievement:"#7C3AED",score:"#F59E0B"}[m.type] || "#7C3AED") }}>{m.label}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {filteredAchievements.map((ach) => {
            const datetime = formatDateTime(ach.earnedAt);
            const achMs = milestoneMap[ach.id] || null;
            const msColor = achMs ? (achMs[0].platform ? ({xbox:"#107C10",playstation:"#2E6DB4",steam:"#66C0F4"}[achMs[0].platform] || "#7C3AED") : ({achievement:"#7C3AED",score:"#F59E0B"}[achMs[0].type] || "#7C3AED")) : null;
            return (
              <Card
                key={ach.id}
                className={`border-white/5 hover:bg-[#121212] transition-colors cursor-pointer ${
                  ach.isUnlocked 
                    ? "bg-green-500/15 border-green-500/30" 
                    : "bg-[#0A0A0A] opacity-70"
                }`}
                style={achMs ? { boxShadow: `inset 0 0 0 1.5px ${msColor}50, 0 0 6px ${msColor}15` } : undefined}
                onClick={() => navigate(`/games/${ach.targetGameId || ach.gameId}`)}
                data-testid={`achievement-card-${ach.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex gap-3">
                    {/* Achievement Image with Score Overlay */}
                    <div className="relative shrink-0">
                      <div className={`w-14 h-14 rounded-lg overflow-hidden ${ach.isUnlocked ? "" : "grayscale"}`}>
                        {ach.imageUrl ? (
                          <AchievementImage
                            src={ach.imageUrl}
                            additionalImages={ach.additionalImages}
                            alt={ach.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center">
                            <Trophy className="w-6 h-6 text-white/20" />
                          </div>
                        )}
                      </div>
                      {/* Score Badge on Image */}
                      <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded bg-black/80 border border-white/10">
                        {getScoreDisplay(ach, true)}
                      </div>
                    </div>
                    
                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        {/* Left: Achievement Info */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white truncate">
                              {ach.name}
                            </h3>
                            {ach.isSecret && (
                              <span className="px-1.5 py-0.5 text-[10px] bg-purple-500/20 text-purple-300 rounded">
                                Secret
                              </span>
                            )}
                            {achMs && <MilestoneBadge milestones={achMs} />}
                          </div>
                          <p className="text-white/50 text-sm truncate">
                            {ach.description || "No description"}
                          </p>
                          
                          {/* Tags */}
                          {ach.tags && ach.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {ach.tags.map((t, i) => (
                                <span
                                  key={i}
                                  className="px-1.5 py-0.5 text-[10px] bg-white/10 text-white/60 rounded"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Right: Game Info */}
                        <div className="text-right shrink-0">
                          {/* Game Name */}
                          <p className="text-white/70 text-sm max-w-[200px] truncate">
                            {ach.gameName}
                          </p>
                          
                          {/* DLC */}
                          {ach.dlcName && (
                            <p className="text-purple-300/90 text-xs truncate flex items-center justify-end gap-1" data-testid={`achievement-card-dlc-${ach.id}`}>
                              <span className="px-1 py-0.5 text-[9px] rounded bg-purple-500/20 border border-purple-300/30 uppercase tracking-wide">DLC</span>
                              <span className="truncate">{ach.dlcName}</span>
                            </p>
                          )}
                          
                          {/* Platform & System */}
                          <div className="flex items-center gap-1 justify-end mt-1">
                            {ach.gamePlatform && (
                              <PlatformBadge platform={ach.gamePlatform} size="sm" />
                            )}
                            {ach.gameSystem && (
                              <SystemBadge system={ach.gameSystem} platform={ach.gamePlatform} size="sm" />
                            )}
                          </div>
                          
                          {/* Unlock Date/Time */}
                          {datetime && (
                            <div className="flex items-center gap-1 justify-end mt-1 text-xs text-white/40">
                              <Calendar className="w-3 h-3" />
                              <span>{datetime.date}</span>
                              <span>{datetime.time}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Bottom Pagination */}
      {totalPages > 1 && paginationControls}

      {/* AdSense */}
      <AdBanner />
    </div>
  );
}


function GameSearchFilter({ games, value, onChange }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selectedGame = games.find((g) => g.id === value);
  const filtered = search
    ? games.filter((g) => g.name.toLowerCase().includes(search.toLowerCase())).slice(0, 30)
    : games.slice(0, 30);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="game-search-filter">
      <div
        className="flex items-center gap-1.5 w-[200px] h-9 px-3 rounded-md border border-white/10 bg-[#121212] cursor-pointer"
        onClick={() => { setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
      >
        <Search className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
        {isOpen ? (
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search games..."
            className="bg-transparent border-none outline-none text-sm text-white/80 w-full placeholder:text-white/30"
            data-testid="game-search-input"
          />
        ) : (
          <span className={`text-sm truncate ${selectedGame ? "text-white/80" : "text-white/40"}`}>
            {selectedGame ? selectedGame.name : "All Games"}
          </span>
        )}
        {value && (
          <button
            onClick={(e) => { e.stopPropagation(); onChange(""); setSearch(""); setIsOpen(false); }}
            className="p-0.5 hover:bg-white/10 rounded"
          >
            <X className="w-3 h-3 text-white/40" />
          </button>
        )}
      </div>
      {isOpen && (
        <div className="absolute top-full mt-1 left-0 w-[280px] max-h-[300px] overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-md z-50 shadow-xl">
          <button
            className="w-full text-left px-3 py-2 text-sm text-white/60 hover:bg-white/5"
            onClick={() => { onChange(""); setSearch(""); setIsOpen(false); }}
          >
            All Games
          </button>
          {filtered.map((g) => (
            <button
              key={g.id}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 truncate ${g.id === value ? "text-violet-400" : "text-white/80"}`}
              onClick={() => { onChange(g.id); setSearch(""); setIsOpen(false); }}
            >
              {g.name}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-2 text-sm text-white/30">No games found</p>
          )}
        </div>
      )}
    </div>
  );
}
